import { Breadcrumb, Flex, GridAlpha } from '@applyboard/crystal-ui';
import { ApplicationDetailsTabs } from '../components/ApplicationDetailsTabs/ApplicationDetailsTabs';
import { useAppProcessing } from '~/pages/ApplicationDetails/context';
import { UniqueApplicationsListProvider } from '../context/UniqueApplicationsListProvider';
import React from 'react';

import {
  ProgramDetails,
  ApplicationDetailsHeading,
  StatusAndCitizenship,
  ContactInformation,
  ApplicantInformation,
  EducationHistory,
  LanguageProficiency,
  OtherRequiredDocuments,
  ApplicationProcessing,
  CardControlActions,
  DecisionLetters,
  UploadAdditionalDocuments,
  EvaluationTab,
  // processing
  AdditionalDocumentNotice,
  DeferralRequestNotice,
  DocumentRequestModeNotice,
  UniqueApplicationsNotice,
  UniqueApplicationsListTable,
} from './../components';
import { ApplicationAssignment } from '../components/Cards/ApplicationAssignment';
import { AlternateProgramInformation } from '../components/Cards/AlternateProgramInformation';
import { ApplicationTimeline } from '../components/Cards/ApplicationTimeline';
import { BreadcrumbItem } from '~/utils';
import { useGetCustomizedDocumentTypes } from '../data/useGetCustomizedDocumentTypes';
import { useSchoolCustomization } from '~/context/SchoolCustomizationsProvider';

type DesktopLayoutProps = { programDetailsLineSplit: boolean; breadcrumbItems: BreadcrumbItem[] };
export function DesktopLayout(props: DesktopLayoutProps) {
  const { application } = useAppProcessing();
  const { customizedDocumentTypes } = useGetCustomizedDocumentTypes();
  const { schoolCustomizations } = useSchoolCustomization();
  const isSkipAlternateProgram = schoolCustomizations?.schoolCustomization?.isSkipAlternateProgram ?? false;
  const transcriptAiEnabled = (() => {
    const docTypes = (
      customizedDocumentTypes?.attributes as
        | {
            educationHistoryDocumentTypes?: Array<{
              type?: string;
              AiProcessDocumentEnabled?: boolean;
              aiProcessDocumentEnabled?: boolean;
            }>;
          }
        | undefined
    )?.educationHistoryDocumentTypes;
    const transcriptDoc = Array.isArray(docTypes) ? docTypes.find((d) => d.type === 'TRANSCRIPT') : undefined;
    if (!transcriptDoc) return true;
    return (
      transcriptDoc.AiProcessDocumentEnabled ??
      (transcriptDoc as { aiProcessDocumentEnabled?: boolean }).aiProcessDocumentEnabled ??
      true
    );
  })();
  const shouldRenderAIMLFeature =
    Boolean(application.statusAndCitizenship.documents[0]) &&
    Boolean(application.statusAndCitizenship.documents[0].documentReviewSuggestion);

  const passportId =
    application.statusAndCitizenship.passportId !== undefined
      ? application.statusAndCitizenship.passportId
      : 'NOT_AVAILABLE';

  const currentApplicationId = application.id;
  const listRef = React.useRef<HTMLDivElement>(null);

  function scrollToApplicationsTable() {
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function applicationDetailsLayout() {
    return (
      <Flex gap={8} align="stretch" direction="column">
        <GridAlpha columns="repeat(3, minmax(0, 1fr))" areas={['left right']} columnGap={10}>
          <GridAlpha.Item areaName="left" columnStart={1} columnEnd={3}>
            <Flex gap={8} direction="column">
              <ApplicantInformation />
              <ContactInformation />
              {!shouldRenderAIMLFeature && <StatusAndCitizenship />}
              <EducationHistory />
              <LanguageProficiency />
              <OtherRequiredDocuments />
            </Flex>
          </GridAlpha.Item>
          <GridAlpha.Item areaName="right" columnStart={3} columnEnd={3}>
            <Flex gap={8} direction="column">
              <ApplicationTimeline />
              {!isSkipAlternateProgram && <AlternateProgramInformation />}
              <ApplicationAssignment />
            </Flex>
          </GridAlpha.Item>
        </GridAlpha>
        <div ref={listRef}>{passportId !== 'NOT_AVAILABLE' && <UniqueApplicationsListTable />}</div>
      </Flex>
    );
  }
  return (
    <div aria-label="desktop" style={{ marginBottom: '10px' }}>
      <UniqueApplicationsListProvider passportId={passportId} currentApplicationId={currentApplicationId}>
        <Flex pt={10} pb={2} px={8} gap={6} direction="column">
          <Breadcrumb items={props.breadcrumbItems} />
        </Flex>
        <Flex px={8} gap={6} direction="column">
          <Flex justify="between" align="center" gap={2}>
            <ApplicationDetailsHeading />
            <ApplicationProcessing />
          </Flex>
          <DeferralRequestNotice />
          <AdditionalDocumentNotice />
          <DocumentRequestModeNotice />
          <ProgramDetails lineSplit={props.programDetailsLineSplit} />
          {passportId !== 'NOT_AVAILABLE' && <UniqueApplicationsNotice onViewTableClick={scrollToApplicationsTable} />}
          <CardControlActions />
          <ApplicationDetailsTabs
            submittedApplicationDetails={applicationDetailsLayout()}
            applicationDetails={
              <Flex gap={8} align="stretch" direction="column">
                <DecisionLetters />
                <UploadAdditionalDocuments />
              </Flex>
            }
            evaluationDetails={<EvaluationTab />}
            showEvaluationTab={transcriptAiEnabled}
          />
        </Flex>
      </UniqueApplicationsListProvider>
    </div>
  );
}
